import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { settingApi } from "../../api/api";

// API service with error handling
const themeService = {
  async saveTheme(theme) {
    try {
      const themeData = {
        primary: theme.primary,
        background: theme.background,
        text: theme.text,
        isDark: theme.isDark,
      };
      const response = await settingApi.themes(themeData);
      return response;
    } catch (error) {
      console.error("Error saving theme to backend:", error);
      this.saveToLocalStorage(theme);
      throw error;
    }
  },

  async getTheme() {
    try {
      const response = await settingApi.getTheme();
      return response.data;
    } catch (error) {
      console.error("Error fetching theme from backend:", error);
      return this.getFromLocalStorage();
    }
  },

  saveToLocalStorage(theme) {
    try {
      localStorage.setItem("appTheme", JSON.stringify(theme));
      localStorage.setItem("appTheme_timestamp", new Date().toISOString());
    } catch (error) {
      console.error("Error saving to localStorage:", error);
    }
  },

  getFromLocalStorage() {
    try {
      const saved = localStorage.getItem("appTheme");
      return saved ? JSON.parse(saved) : null;
    } catch (error) {
      console.error("Error reading from localStorage:", error);
      return null;
    }
  },

  getLastSaveTime() {
    try {
      return localStorage.getItem("appTheme_timestamp");
    } catch (error) {
      return null;
    }
  }
};

const MobileTheme = () => {
  // State management
  const [theme, setTheme] = useState({
    primary: "#3498db",
    text: "#000000",
    background: "#ffffff",
    isDark: false
  });
  const [themeModalVisible, setThemeModalVisible] = useState(false);
  const [selectedTab, setSelectedTab] = useState("quick");
  const [colorInput, setColorInput] = useState("#3498db");
  const [isMobile, setIsMobile] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const [lastSaved, setLastSaved] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Check screen size
  useEffect(() => {
    const checkScreenSize = () => setIsMobile(window.innerWidth < 768);
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Load theme on component mount
  useEffect(() => {
    loadUserTheme();
  }, []);

  // Load user theme
  const loadUserTheme = async () => {
    try {
      const userTheme = await settingApi.getAllmobileThemes(0);
      const backendTheme = userTheme.data.theme;
      
      if (backendTheme && typeof backendTheme === 'object') {
        const validatedTheme = {
          primary: backendTheme.primary || "#3498db",
          text: backendTheme.text || "#000000",
          background: backendTheme.background || "#ffffff",
          isDark: backendTheme.isDark || false
        };
        
        setTheme(validatedTheme);
        setColorInput(validatedTheme.primary);
        setLastSaved(themeService.getLastSaveTime());
        
      } else {
        throw new Error("Invalid theme data");
      }
    } catch (error) {
    
      const localTheme = themeService.getFromLocalStorage();
      if (localTheme) {
        setTheme(localTheme);
        setColorInput(localTheme.primary);
        setLastSaved(themeService.getLastSaveTime());
      }
    }
  };

  // Manual save handler
  const saveCurrentTheme = async () => {
    if (!hasUnsavedChanges) {
      setSaveMessage("No changes to save");
      setTimeout(() => setSaveMessage(""), 2000);
      return;
    }

    setIsLoading(true);
    setSaveMessage("");
    
    try {
      themeService.saveToLocalStorage(theme);
      const result = await themeService.saveTheme(theme);
      setSaveMessage("Theme saved successfully! ✅");
      setLastSaved(new Date().toISOString());
      setHasUnsavedChanges(false);
      
      setTimeout(() => setSaveMessage(""), 3000);
    } catch (error) {
      setSaveMessage("Failed to save theme ❌");
      setTimeout(() => setSaveMessage(""), 3000);
    } finally {
      setIsLoading(false);
    }
  };

  // Theme modification handlers
  const updateTheme = (newTheme) => {
    setTheme(newTheme);
    setHasUnsavedChanges(true);
  };

  const changeColor = (color) => {
    updateTheme({ ...theme, primary: color });
    setColorInput(color);
  };

  const applyTheme = (newTheme) => {
    updateTheme(newTheme);
    setColorInput(newTheme.primary);
  };

  const applyColor = () => {
    if (/^#[0-9A-F]{6}$/i.test(colorInput)) {
      updateTheme({ ...theme, primary: colorInput });
    } else {
      alert("Please enter a valid hex color (e.g., #3498db)");
    }
  };

  const toggleDarkMode = () => {
    const newTheme = theme.isDark
      ? { ...theme, background: "#ffffff", text: "#000000", isDark: false }
      : { ...theme, background: "#1a1a1a", text: "#ffffff", isDark: true };
    updateTheme(newTheme);
  };

  const resetToDefault = () => {
    const defaultTheme = {
      primary: "#3498db",
      background: "#ffffff",
      text: "#000000",
      isDark: false
    };
    updateTheme(defaultTheme);
    setColorInput("#3498db");
  };

  // Color palettes
  const quickColors = [
    "#3498db", "#2980b9", "#1f618d", "#85c1e9", "#5dade2",
    "#e74c3c", "#c0392b", "#f1948a", "#ec7063", "#f5b7b1",
    "#2ecc71", "#27ae60", "#229954", "#82e0aa", "#58d68d",
    "#9b59b6", "#8e44ad", "#bb8fce", "#af7ac5", "#d7bde2",
    "#f1c40f", "#f39c12", "#e67e22", "#f7dc6f", "#f8c471",
    "#1abc9c", "#16a085", "#76d7c4", "#48c9b0", "#a3e4d7",
    "#95a5a6", "#7f8c8d", "#bdc3c7", "#ecf0f1", "#34495e"
  ];

  const presetThemes = [
    { name: "Classic Blue", background: "#ffffff", primary: "#3498db", text: "#000000", isDark: false },
    { name: "Dark Mode", background: "#1a1a1a", primary: "#bb86fc", text: "#ffffff", isDark: true },
    { name: "Nature Green", background: "#f8fff8", primary: "#27ae60", text: "#1a1a1a", isDark: false },
    { name: "Sunset Orange", background: "#fff8f0", primary: "#e67e22", text: "#333333", isDark: false },
    { name: "Royal Purple", background: "#faf5ff", primary: "#9b59b6", text: "#2c2c2c", isDark: false },
    { name: "Ocean Teal", background: "#f0fdff", primary: "#1abc9c", text: "#1a1a1a", isDark: false },
    { name: "Warm Red", background: "#fff5f5", primary: "#e74c3c", text: "#2c2c2c", isDark: false },
    { name: "Professional Gray", background: "#f8f9fa", primary: "#34495e", text: "#2c3e50", isDark: false }
  ];

  const colorCategories = [
    { name: "Blues", colors: quickColors.slice(0, 5) },
    { name: "Reds", colors: quickColors.slice(5, 10) },
    { name: "Greens", colors: quickColors.slice(10, 15) },
    { name: "Purples", colors: quickColors.slice(15, 20) },
    { name: "Warm Colors", colors: quickColors.slice(20, 25) },
    { name: "Cool Colors", colors: quickColors.slice(25, 30) },
    { name: "Grays", colors: quickColors.slice(30, 35) }
  ];

  // Helper functions
  const getSecondaryColor = () => theme.isDark ? "#2d2d2d" : "#f8f9fa";
  const formatLastSaved = () => lastSaved ? new Date(lastSaved).toLocaleTimeString() : "Never";

  // Components
  const ActionButton = ({ children, onClick, style, disabled, ...props }) => (
    <button
      onClick={onClick}
      disabled={disabled || isLoading}
      style={{
        ...style,
        opacity: (disabled || isLoading) ? 0.6 : 1,
        cursor: (disabled || isLoading) ? 'not-allowed' : 'pointer',
        transition: 'all 0.3s ease'
      }}
      {...props}
    >
      {isLoading ? '⏳ Saving...' : children}
    </button>
  );

  const ColorSwatch = ({ color, isSelected, onClick }) => (
    <div
      onClick={onClick}
      style={{
        width: isMobile ? "32px" : "44px",
        height: isMobile ? "32px" : "44px",
        borderRadius: isMobile ? "8px" : "12px",
        backgroundColor: color,
        cursor: "pointer",
        border: isSelected ? `3px solid ${theme.text}` : `2px solid ${theme.text}20`,
        transition: "all 0.2s ease",
        boxShadow: isSelected ? `0 0 0 2px ${theme.background}` : "none"
      }}
      title={color}
    />
  );

  // Save Theme Button Component
  const SaveThemeButton = () => (
    <ActionButton
      onClick={saveCurrentTheme}
      disabled={!hasUnsavedChanges || isLoading}
      style={{
        backgroundColor: hasUnsavedChanges ? "#4CAF50" : "#95a5a6",
        padding: isMobile ? "12px 20px" : "15px 25px",
        color: "white",
        border: "none",
        borderRadius: "8px",
        fontWeight: "bold",
        boxShadow: hasUnsavedChanges ? "0 4px 15px rgba(76, 175, 80, 0.3)" : "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "8px",
        width: "100%",
        marginTop: "15px"
      }}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
        <path d="M15,9H5V5H15M12,19A3,3 0 0,1 9,16A3,3 0 0,1 12,13A3,3 0 0,1 15,16A3,3 0 0,1 12,19M17,3H5C3.89,3 3,3.9 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V7L17,3Z"/>
      </svg>
      {hasUnsavedChanges ? "Save Theme Changes" : "No Changes to Save"}
    </ActionButton>
  );

  // Responsive styles
  const styles = {
    container: {
      padding: isMobile ? "20px 15px" : "30px 20px",
      minHeight: "100vh"
    },
    header: {
      fontSize: isMobile ? "2rem" : "2.5rem",
      marginBottom: isMobile ? "8px" : "10px"
    },
    subtitle: {
      fontSize: isMobile ? "0.95rem" : "1.1rem",
      lineHeight: "1.4"
    },
    grid: {
      display: isMobile ? "flex" : "grid",
      flexDirection: isMobile ? "column" : "row",
      gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
      gap: isMobile ? "20px" : "30px"
    },
    previewSection: {
      padding: isMobile ? "20px" : "30px",
      borderRadius: isMobile ? "12px" : "16px"
    },
    buttonGroup: {
      display: "flex",
      flexDirection: isMobile ? "column" : "row",
      gap: isMobile ? "10px" : "15px",
      flexWrap: "wrap"
    },
    actionButton: {
      padding: isMobile ? "12px 20px" : "15px 25px",
      fontSize: isMobile ? "0.9rem" : "1rem",
      minWidth: isMobile ? "auto" : "150px",
      width: isMobile ? "100%" : "auto"
    },
    colorGrid: {
      display: "grid",
      gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(4, 1fr)",
      gap: isMobile ? "10px" : "12px"
    },
    modal: {
      width: isMobile ? "95%" : "90%",
      maxWidth: isMobile ? "none" : "600px",
      padding: isMobile ? "20px" : "30px"
    }
  };

  return (
    <div style={{
      backgroundColor: "white",
      minHeight: "100vh",
      color: theme.text,
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      transition: "all 0.3s ease",
      ...styles.container
    }}>
      {/* Header Section */}
      <div style={{ textAlign: "center", marginBottom: isMobile ? 30 : 40 }}>
        <h1 style={{ color: "black", ...styles.header, fontWeight: 700 }}>
          Theme Customizer
        </h1>
        <p style={{ ...styles.subtitle, color: "black", opacity: 0.8, maxWidth: 600, margin: "0 auto" }}>
          Personalize your experience with custom colors and themes
        </p>
        
        {/* Last Saved Info */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "15px", marginTop: "15px", flexWrap: "wrap" }}>
          <span style={{ fontSize: "0.8rem", color: "#666", display: "flex", alignItems: "center", gap: "5px" }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="#666">
              <path d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M11,16.5L18,9.5L16.59,8.09L11,13.67L7.91,10.59L6.5,12L11,16.5Z"/>
            </svg>
            Last saved: {formatLastSaved()}
          </span>
        </div>

        {/* Unsaved Changes Banner */}
        {hasUnsavedChanges && (
          <div style={{
            background: "#ffa500",
            color: "white",
            padding: "10px 15px",
            borderRadius: "8px",
            margin: "15px auto 0 auto",
            maxWidth: "400px",
            fontSize: "0.8rem",
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px"
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <path d="M11,9H13V7H11M12,20C7.59,20 4,16.41 4,12C4,7.59 7.59,4 12,4C16.41,4 20,7.59 20,12C20,16.41 16.41,20 12,20M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M11,17H13V11H11V17Z"/>
            </svg>
            You have unsaved changes - Click "Save Theme Changes" to save
          </div>
        )}
      </div>

      <div style={styles.grid}>
        {/* Preview Section */}
        <div style={{
          background: getSecondaryColor(),
          ...styles.previewSection,
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
          border: `1px solid ${theme.primary}20`
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
            <h2 style={{ color: theme.primary, margin: 0, fontSize: isMobile ? "1.3rem" : "1.5rem" }}>
              Live Preview
            </h2>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              {hasUnsavedChanges && (
                <span style={{ background: "#ffa500", color: "white", padding: "4px 8px", borderRadius: "12px", fontSize: "0.7rem", fontWeight: "bold" }}>
                  UNSAVED CHANGES
                </span>
              )}
            </div>
          </div>

          {/* Mobile Preview */}
          <div style={{ marginBottom: 25, display: "flex", flexDirection: "column", alignItems: "center" }}>
            <h3 style={{ marginBottom: 15, fontSize: isMobile ? "1.1rem" : "1.2rem", textAlign: "center" }}>
              Mobile Preview
            </h3>
            
            <div style={{
              width: isMobile ? "280px" : "320px",
              height: isMobile ? "500px" : "600px",
              backgroundColor: "#f0f0f0",
              borderRadius: "25px",
              padding: "15px",
              border: "8px solid #333",
              boxShadow: "0 10px 30px rgba(0,0,0,0.3)",
              position: "relative",
              overflow: "hidden",
              marginBottom: 15
            }}>
              {/* Mobile Status Bar */}
              <div style={{
                height: "25px",
                backgroundColor: theme.background,
                borderRadius: "15px 15px 0 0",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "0 15px",
                fontSize: "0.7rem",
                fontWeight: "bold"
              }}>
                <span style={{ color: theme.text }}>9:41</span>
                <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill={theme.text}>
                    <path d="M3,9h2V19H3V9M7,13h2v6H7V13M11,5h2v14h-2V5M15,9h2v10h-2V9M19,13h2v6h-2V13"/>
                  </svg>
                  <svg width="16" height="12" viewBox="0 0 24 24" fill={theme.text}>
                    <path d="M16,8H4V16H16V8M16.67,6H3.33C2.6,6 2,6.6 2,7.33V16.67C2,17.4 2.6,18 3.33,18H16.67C17.4,18 18,17.4 18,16.67V7.33C18,6.6 17.4,6 16.67,6M21,10V14H23V10H21Z"/>
                  </svg>
                </div>
              </div>

              {/* Mobile Content */}
              <div style={{
                height: "calc(100% - 25px)",
                backgroundColor: theme.background,
                borderRadius: "0 0 15px 15px",
                padding: "20px",
                overflowY: "auto"
              }}>
                {/* App Header */}
                <div style={{ textAlign: "center", marginBottom: "20px", paddingBottom: "15px", borderBottom: `2px solid ${theme.primary}30` }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "10px" }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill={theme.primary}>
                      <path d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M11,16.5L18,9.5L16.59,8.09L11,13.67L7.91,10.59L6.5,12L11,16.5Z"/>
                    </svg>
                    <h4 style={{ color: theme.primary, margin: 0, fontSize: "1.1rem" }}>My App</h4>
                  </div>
                </div>

                {/* Content */}
                <div style={{ marginBottom: "20px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px" }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill={theme.primary}>
                      <path d="M12,4A4,4 0 0,1 16,8A4,4 0 0,1 12,12A4,4 0 0,1 8,8A4,4 0 0,1 12,4M12,14C16.42,14 20,15.79 20,18V20H4V18C4,15.79 7.58,14 12,14Z"/>
                    </svg>
                    <h5 style={{ color: theme.primary, margin: 0, fontSize: "0.9rem" }}>Welcome</h5>
                  </div>
                  <p style={{ fontSize: "0.8rem", lineHeight: "1.4", color: theme.text, marginBottom: "15px" }}>
                    This is how your app looks with the current theme.
                  </p>
                </div>

                {/* Buttons */}
                <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginBottom: "20px" }}>
                  <button style={{
                    backgroundColor: theme.primary,
                    color: "white",
                    padding: "12px",
                    borderRadius: "8px",
                    border: "none",
                    fontSize: "0.8rem",
                    fontWeight: "600",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px"
                  }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z"/>
                    </svg>
                    Primary Action
                  </button>
                  <button style={{
                    backgroundColor: "transparent",
                    color: theme.primary,
                    padding: "12px",
                    borderRadius: "8px",
                    border: `2px solid ${theme.primary}`,
                    fontSize: "0.8rem",
                    fontWeight: "600",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px"
                  }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill={theme.primary}>
                      <path d="M12,15.5A3.5,3.5 0 0,1 8.5,12A3.5,3.5 0 0,1 12,8.5A3.5,3.5 0 0,1 15.5,12A3.5,3.5 0 0,1 12,15.5M19.43,12.97C19.47,12.65 19.5,12.33 19.5,12C19.5,11.67 19.47,11.34 19.43,11L21.54,9.37C21.73,9.22 21.78,8.95 21.66,8.73L19.66,5.27C19.54,5.05 19.27,4.96 19.05,5.05L16.56,6.05C16.04,5.66 15.5,5.32 14.87,5.07L14.5,2.42C14.46,2.18 14.25,2 14,2H10C9.75,2 9.54,2.18 9.5,2.42L9.13,5.07C8.5,5.32 7.96,5.66 7.44,6.05L4.95,5.05C4.73,4.96 4.46,5.05 4.34,5.27L2.34,8.73C2.22,8.95 2.27,9.22 2.46,9.37L4.57,11C4.53,11.34 4.5,11.67 4.5,12C4.5,12.33 4.53,12.65 4.57,12.97L2.46,14.63C2.27,14.78 2.22,15.05 2.34,15.27L4.34,18.73C4.46,18.95 4.73,19.03 4.95,18.95L7.44,17.94C7.96,18.34 8.5,18.68 9.13,18.93L9.5,21.58C9.54,21.82 9.75,22 10,22H14C14.25,22 14.46,21.82 14.5,21.58L14.87,18.93C15.5,18.68 16.04,18.34 16.56,17.94L19.05,18.95C19.27,19.03 19.54,18.95 19.66,18.73L21.66,15.27C21.78,15.05 21.73,14.78 21.54,14.63L19.43,12.97Z"/>
                    </svg>
                    Secondary Action
                  </button>
                </div>

                {/* Card */}
                <div style={{
                  backgroundColor: getSecondaryColor(),
                  borderRadius: "10px",
                  padding: "15px",
                  marginBottom: "15px",
                  border: `1px solid ${theme.primary}20`
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
                    <div style={{
                      width: "30px", height: "30px", borderRadius: "50%", backgroundColor: theme.primary,
                      display: "flex", alignItems: "center", justifyContent: "center"
                    }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
                        <path d="M12,4A4,4 0 0,1 16,8A4,4 0 0,1 12,12A4,4 0 0,1 8,8A4,4 0 0,1 12,4M12,14C16.42,14 20,15.79 20,18V20H4V18C4,15.79 7.58,14 12,14Z"/>
                      </svg>
                    </div>
                    <div>
                      <h6 style={{ margin: 0, fontSize: "0.8rem", color: theme.text }}>Card Title</h6>
                      <p style={{ margin: 0, fontSize: "0.7rem", opacity: 0.7 }}>Subtitle</p>
                    </div>
                  </div>
                  <p style={{ fontSize: "0.75rem", lineHeight: "1.3", margin: 0 }}>
                    This card shows how your theme appears in mobile components.
                  </p>
                </div>

                {/* Bottom Navigation */}
                <div style={{
                  position: "absolute", bottom: "10px", left: "20px", right: "20px",
                  backgroundColor: getSecondaryColor(), borderRadius: "15px", padding: "10px",
                  display: "flex", justifyContent: "space-around"
                }}>
                  {["Home", "Search", "Favorites", "Profile"].map((label, index) => (
                    <button key={index} style={{ background: "none", border: "none", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill={index === 0 ? theme.primary : theme.text} opacity={index === 0 ? 1 : 0.5}>
                        <path d={
                          index === 0 ? "M10,20V14H14V20H19V12H22L12,3L2,12H5V20H10Z" :
                          index === 1 ? "M9.5,3A6.5,6.5 0 0,1 16,9.5C16,11.11 15.41,12.59 14.44,13.73L14.71,14H15.5L20.5,19L19,20.5L14,15.5V14.71L13.73,14.44C12.59,15.41 11.11,16 9.5,16A6.5,6.5 0 0,1 3,9.5A6.5,6.5 0 0,1 9.5,3M9.5,5C7,5 5,7 5,9.5C5,12 7,14 9.5,14C12,14 14,12 14,9.5C14,7 12,5 9.5,5Z" :
                          index === 2 ? "M12,21.35L10.55,20.03C5.4,15.36 2,12.28 2,8.5C2,5.42 4.42,3 7.5,3C9.24,3 10.91,3.81 12,5.09C13.09,3.81 14.76,3 16.5,3C19.58,3 22,5.42 22,8.5C22,12.28 18.6,15.36 13.45,20.04L12,21.35Z" :
                          "M12,4A4,4 0 0,1 16,8A4,4 0 0,1 12,12A4,4 0 0,1 8,8A4,4 0 0,1 12,4M12,14C16.42,14 20,15.79 20,18V20H4V18C4,15.79 7.58,14 12,14Z"
                        }/>
                      </svg>
                      <span style={{ fontSize: "0.6rem", color: index === 0 ? theme.primary : theme.text, opacity: index === 0 ? 1 : 0.5 }}>
                        {label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <p style={{ fontSize: "0.8rem", opacity: 0.7, textAlign: "center", marginTop: "10px" }}>
              Real-time mobile preview with your theme colors
            </p>
          </div>

          {/* Save Theme Button */}
          <SaveThemeButton />

          {/* Save Status */}
          <div style={{ marginTop: 20, padding: "15px", background: getSecondaryColor(), borderRadius: "8px", border: `1px solid ${theme.primary}20` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
              <span style={{ fontWeight: "bold", color: theme.text }}>Save Status</span>
              <span style={{ fontSize: "0.8rem", color: theme.text, opacity: 0.7 }}>
                Manual Save Required
              </span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "0.8rem" }}>
              <span style={{ color: theme.text, opacity: 0.7 }}>Last saved: {formatLastSaved()}</span>
            </div>
            {saveMessage && (
              <div style={{
                marginTop: "10px",
                padding: "8px",
                borderRadius: "4px",
                background: saveMessage.includes("✅") ? "#4CAF50" : saveMessage.includes("❌") ? "#f44336" : "#ffa500",
                color: "white",
                fontSize: "0.8rem",
                textAlign: "center"
              }}>
                {saveMessage}
              </div>
            )}
          </div>
        </div>

        {/* Controls Section */}
        <div>
          {/* Action Buttons */}
          <div style={styles.buttonGroup}>
            <ActionButton
              onClick={() => setThemeModalVisible(true)}
              style={{
                backgroundColor: theme.primary,
                ...styles.actionButton,
                color: "white",
                border: "none",
                fontWeight: "bold",
                boxShadow: `0 4px 15px ${theme.primary}40`,
              }}
            >
              🎨 Customize Theme
            </ActionButton>
            <ActionButton
              onClick={toggleDarkMode}
              style={{
                backgroundColor: getSecondaryColor(),
                ...styles.actionButton,
                color: theme.text,
                border: `2px solid ${theme.text}20`,
                fontWeight: "bold",
              }}
            >
              {theme.isDark ? "☀️ Light Mode" : "🌙 Dark Mode"}
            </ActionButton>
            <ActionButton
              onClick={resetToDefault}
              style={{
                backgroundColor: getSecondaryColor(),
                ...styles.actionButton,
                color: theme.text,
                border: `2px solid ${theme.text}20`,
                fontWeight: "bold",
              }}
            >
              🔄 Reset to Default
            </ActionButton>
          </div>

          {/* Quick Colors */}
          <div style={{
            background: getSecondaryColor(),
            padding: isMobile ? 20 : 25,
            borderRadius: 16,
            boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
            marginBottom: 25
          }}>
            <h3 style={{ marginBottom: 20, fontSize: isMobile ? "1.2rem" : "1.3rem" }}>
              Quick Color Picker
            </h3>
            {colorCategories.map((category, index) => (
              <div key={index} style={{ marginBottom: 20 }}>
                <h4 style={{ marginBottom: 12, fontSize: isMobile ? "0.85rem" : "0.9rem", color: theme.text, opacity: 0.8 }}>
                  {category.name}
                </h4>
                <div style={{ display: "flex", gap: isMobile ? "6px" : "10px", flexWrap: "wrap" }}>
                  {category.colors.map((color, i) => (
                    <ColorSwatch
                      key={i}
                      color={color}
                      isSelected={theme.primary === color}
                      onClick={() => changeColor(color)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Preset Themes */}
          <div>
            <h3 style={{ marginBottom: 15, fontSize: isMobile ? "1.2rem" : "1.3rem" }}>
              Popular Themes
            </h3>
            <div style={styles.colorGrid}>
              {presetThemes.slice(0, isMobile ? 4 : 8).map((themeOption, i) => (
                <div
                  key={i}
                  onClick={() => applyTheme(themeOption)}
                  style={{
                    padding: isMobile ? "15px" : "20px",
                    background: themeOption.background,
                    cursor: "pointer",
                    border: `2px solid ${theme.primary}30`,
                    transition: "all 0.3s ease",
                    textAlign: "center",
                    borderRadius: 12
                  }}
                  onMouseEnter={(e) => {
                    if (!isMobile) {
                      e.currentTarget.style.transform = "translateY(-2px)";
                      e.currentTarget.style.boxShadow = "0 8px 25px rgba(0,0,0,0.15)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isMobile) {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "none";
                    }
                  }}
                >
                  <div style={{ display: "flex", gap: 6, justifyContent: "center", marginBottom: 10 }}>
                    <div style={{ width: 20, height: 20, borderRadius: "50%", background: themeOption.primary, border: `2px solid ${themeOption.background}` }}></div>
                    <div style={{ width: 20, height: 20, borderRadius: "50%", background: themeOption.text, border: `2px solid ${themeOption.background}` }}></div>
                  </div>
                  <p style={{ margin: 0, fontWeight: "bold", fontSize: isMobile ? "0.85rem" : "0.9rem", color: themeOption.text }}>
                    {themeOption.name}
                  </p>
                  <p style={{ margin: "5px 0 0 0", fontSize: "0.7rem", color: themeOption.text, opacity: 0.6 }}>
                    {themeOption.isDark ? "Dark" : "Light"}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Theme Modal */}
      {themeModalVisible && createPortal(
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)",
          display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 1000, padding: isMobile ? 10 : 20
        }} onClick={() => setThemeModalVisible(false)}>
          <div style={{
            background: theme.background, color: theme.text, ...styles.modal,
            borderRadius: isMobile ? 16 : 20, maxHeight: "90vh", overflowY: "auto",
            boxShadow: "0 20px 60px rgba(0,0,0,0.3)"
          }} onClick={(e) => e.stopPropagation()}>
            
            {/* Modal Header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 25, paddingBottom: 20, borderBottom: `1px solid ${theme.text}20` }}>
              <h2 style={{ margin: 0, color: theme.primary, fontSize: isMobile ? "1.3rem" : "1.5rem" }}>
                Theme Customization
              </h2>
              <button onClick={() => setThemeModalVisible(false)} style={{ background: "none", border: "none", fontSize: isMobile ? 20 : 24, cursor: "pointer", color: theme.text, opacity: 0.7 }}>
                ✕
              </button>
            </div>

            {/* Tabs */}
            <div style={{ display: "flex", background: getSecondaryColor(), borderRadius: 12, padding: 6, marginBottom: 25, flexDirection: isMobile ? "column" : "row", gap: isMobile ? "5px" : "0" }}>
              {["quick", "custom", "presets"].map((tab) => (
                <button key={tab} onClick={() => setSelectedTab(tab)} style={{
                  flex: 1, padding: isMobile ? "10px" : "12px", background: selectedTab === tab ? theme.primary : "transparent",
                  color: selectedTab === tab ? "#fff" : theme.text, border: "none", borderRadius: 8, cursor: "pointer",
                  fontWeight: "bold", fontSize: isMobile ? "0.85rem" : "0.9rem"
                }}>
                  {tab === "quick" && "🎨 Quick Colors"}
                  {tab === "custom" && "🔧 Custom Color"}
                  {tab === "presets" && "🌟 Preset Themes"}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            {selectedTab === "quick" && (
              <div>
                <h3 style={{ marginBottom: 20, fontSize: isMobile ? "1.2rem" : "1.3rem" }}>Color Palette</h3>
                {colorCategories.map((category, index) => (
                  <div key={index} style={{ marginBottom: 25 }}>
                    <h4 style={{ marginBottom: 15, color: theme.text, fontSize: isMobile ? "0.9rem" : "1rem" }}>{category.name}</h4>
                    <div style={{ display: "flex", gap: isMobile ? "6px" : "10px", flexWrap: "wrap" }}>
                      {category.colors.map((color, i) => (
                        <ColorSwatch key={i} color={color} isSelected={theme.primary === color} onClick={() => changeColor(color)} />
                      ))}
                    </div>
                  </div>
                ))}
                {/* Save Button in Quick Colors Tab */}
                <SaveThemeButton />
              </div>
            )}

            {selectedTab === "presets" && (
              <div>
                <h3 style={{ marginBottom: 20, fontSize: isMobile ? "1.2rem" : "1.3rem" }}>Complete Themes</h3>
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: 15 }}>
                  {presetThemes.map((themeOption, i) => (
                    <div key={i} onClick={() => applyTheme(themeOption)} style={{
                      padding: isMobile ? 15 : 20, borderRadius: 12, background: themeOption.background, cursor: "pointer",
                      border: `2px solid ${theme.primary}30`, transition: "all 0.3s ease"
                    }}>
                      <div style={{ display: "flex", gap: 8, marginBottom: 15, justifyContent: "center" }}>
                        <div style={{ width: 24, height: 24, borderRadius: "50%", background: themeOption.primary, border: `2px solid ${themeOption.background}` }}></div>
                        <div style={{ width: 24, height: 24, borderRadius: "50%", background: themeOption.text, border: `2px solid ${themeOption.background}` }}></div>
                      </div>
                      <p style={{ margin: 0, fontWeight: "bold", color: themeOption.text, fontSize: isMobile ? "0.9rem" : "1rem" }}>
                        {themeOption.name}
                      </p>
                      <p style={{ margin: "5px 0 0 0", fontSize: isMobile ? "0.75rem" : "0.8rem", color: themeOption.text, opacity: 0.7 }}>
                        {themeOption.isDark ? "Dark Mode" : "Light Mode"}
                      </p>
                    </div>
                  ))}
                </div>
                {/* Save Button in Presets Tab */}
                <SaveThemeButton />
              </div>
            )}

            {selectedTab === "custom" && (
              <div>
                <h3 style={{ marginBottom: 20, fontSize: isMobile ? "1.2rem" : "1.3rem" }}>Custom Color</h3>
                <div style={{ display: "flex", gap: 20, padding: isMobile ? 15 : 20, background: getSecondaryColor(), borderRadius: 12, alignItems: "center", marginBottom: 20, flexDirection: isMobile ? "column" : "row" }}>
                  <div style={{ width: isMobile ? 60 : 80, height: isMobile ? 60 : 80, borderRadius: 12, backgroundColor: colorInput, display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: "bold" }}>
                    Preview
                  </div>
                  <div>
                    <strong style={{ fontSize: isMobile ? "1rem" : "1.1rem" }}>{colorInput}</strong>
                    <p style={{ margin: "5px 0 0 0", opacity: 0.7, fontSize: isMobile ? "0.8rem" : "0.9rem" }}>Enter a hex color code</p>
                  </div>
                </div>
                <div style={{ position: "relative" }}>
                  <input type="text" value={colorInput} onChange={(e) => setColorInput(e.target.value)} maxLength={7} placeholder="#3498db" style={{
                    padding: isMobile ? "12px 12px 12px 45px" : "15px 15px 15px 50px", width: "100%", borderRadius: 10,
                    border: `2px solid ${theme.text}30`, fontSize: isMobile ? 14 : 16, background: theme.background, color: theme.text
                  }} />
                  <div style={{ position: "absolute", left: isMobile ? 12 : 15, top: "50%", transform: "translateY(-50%)", width: isMobile ? 20 : 24, height: isMobile ? 20 : 24, borderRadius: 6, backgroundColor: colorInput }}></div>
                </div>
                <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
                  <button onClick={applyColor} style={{
                    flex: 1, padding: isMobile ? 12 : 15, backgroundColor: theme.primary, color: "white", border: "none",
                    borderRadius: 10, cursor: "pointer", fontWeight: "bold", fontSize: isMobile ? "0.9rem" : "1rem"
                  }}>
                    Apply Color
                  </button>
                  <ActionButton onClick={saveCurrentTheme} style={{
                    flex: 1, padding: isMobile ? 12 : 15, backgroundColor: "#4CAF50", color: "white", border: "none",
                    borderRadius: 10, fontWeight: "bold", fontSize: isMobile ? "0.9rem" : "1rem"
                  }}>
                    💾 Save
                  </ActionButton>
                </div>
              </div>
            )}
          </div>
        </div>, document.body
      )}

      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
};

export default MobileTheme;